import { View } from "@lernetz/ts-lib";
import { parse } from 'regexparam';
import { IRoute, Router } from "./Router";

export interface ContentFactory<RouterContent> {
    ( params: any, route: Route<RouterContent> ): RouterContent;
}

export interface RouteConfig<RouterContent> {
    pattern: string,
    content: ContentFactory<RouterContent>,
    caching?: boolean,
    name?: string;
}

export class Route<RouterContent> implements IRoute<RouterContent> {

    readonly name: string;

    private pattern: Pattern;
    private content: ContentFactory<RouterContent>;
    private caching: boolean;
    private cache: Map<string, RouterContent>;

    constructor( config: RouteConfig<RouterContent> ) {
        Object.assign( this, { caching: true, cache: new Map() }, config, { pattern: new Pattern( config.pattern ) } );
    }


    invoke( path: string, router: Router<RouterContent>, restoreScroll:boolean ) {

        // check if there is a view already in the cache
        let content = ( this.caching && this.cache.get( path ) ) ? this.cache.get( path ) : this.content( this.pattern.params( path ), this );

        // store in cache
        if( content && this.caching ) this.cache.set( path, content );

        // create route content
        router.content = content;

        // set scrollpos
        router.scrollHistory.setPos( path, restoreScroll );

        // push history when go to a new path
        router.writeURL( path );
    }


    /**
     * Returns true if the given path matches this route definition
     * @param path The path to check
     */
    test( path ) {
        return this.pattern.test( path );
    }

    pathOf( params: {} ) {
        return this.pattern.pathOf( params );
    }
}


interface RedirectAction<RouterContent> {
    ( params: any, route: Redirect<RouterContent>, router: Router<RouterContent> ): void;
}

export interface RedirectConfig<RouterContent> {
    pattern: string,
    action: RedirectAction<RouterContent>;
}

export class Redirect<RouterContent> implements IRoute<RouterContent> {

    pattern: Pattern;
    action: RedirectAction<RouterContent>;

    constructor( config: RedirectConfig<RouterContent> ) {
        this.pattern = new Pattern( config.pattern );
        this.action = config.action;
    }

    test( path: string ): boolean {
        return this.pattern.test( path );
    }
    invoke( path: string, router: Router<RouterContent> ) {
        this.action( this.pattern.params( path ), this, router );
    }
    pathOf( params: any ): string {
        return this.pattern.pathOf( params );
    }

}


export class Pattern {

    private pattern: string;
    private matcher: { pattern: RegExp, keys: string[]; };

    constructor( pattern: string ) {
        this.matcher = parse( pattern );
        this.pattern = pattern;
    }

    /**
     * Tests if the given path matches this pattern
     * @param path The path to test
     */
    test( path: string ) {
        return this.matcher.pattern.test( path );
    }


    /**
     * Extract parameters based on this pattern of the given path
     * @param path the path to extract params
     */
    params( path ) {

        let i = 0, params = {};
        let matches = this.matcher.pattern.exec( path );
        while( i < this.matcher.keys.length ) {
            params[ this.matcher.keys[ i ] ] = matches[ ++i ] || null;
        }

        return params;
    }


    /**
     * Returns a path of this pattern with the given parameters
     * @param params The route pattern parameters
     */
    pathOf( params: {} ) {
        return this.pattern.replace( /(\/:[^\/]+|\/\*)/gm, ( s ) => {
            let key = s.replace( /\/|:|\?/gm, '' );
            return ( params[ key ] ) ? '/' + params[ key ] : '';
        } );
    }
}
